import { Amplify } from 'aws-amplify';
import { QueryClientProvider } from '@tanstack/react-query';
import { NavigationContainer } from '@react-navigation/native';
import NotificationsProvider from './components/notifications/NotificationsProvider';
import RootNavigation from './components/AppNavigator/RootNavigation';
import { SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context';
import { PortalProvider } from '@gorhom/portal';
import React, { useEffect, useMemo, useState } from 'react';
import * as Linking from 'expo-linking';
import { Environment } from './hooks/useEnvironment';
import awsExportsDev from './aws-exports-dev';
import awsExportsProd from './aws-exports-prod';
import { ClientType, getClientType } from './hooks/useClientType';
import { ThemeProvider } from '@aws-amplify/ui-react-native';
import * as WebBrowser from 'expo-web-browser';
import { EventProvider } from 'react-native-outside-press';
import { Platform, StatusBar, View } from 'react-native';
import KeyboardAvoider from './components/shared/KeyboardAvoider';
import { getQueryClient } from './utils/queryClient';
import { getIntl } from './utils/intl';
import { withIsFormDirtyProvider } from './components/shared/FormExitValidation';
import { FixedPositionPortalContainer } from './components/v2BaseComponents/FixedPositionPortalContainer';
import { NestedScrollViewPortalHostContainer } from './components/v2BaseComponents/NestedScrollViewPortal';
import { AnimateLoadedContent } from './components/v2BaseComponents/Loader';
import KeyboardRestrictedView from './components/shared/KeyboardRestrictedView';
import { SideDrawerCollapsedStateProvider } from './components/shared/SideDrawerCollapsedStateProvider';
import { useCaptureAppQueryParams } from './hooks/useUrlAppParams';
import { amplifyUITheme, colors } from './utils/theme';
import ErrorBoundary from './components/v2BaseComponents/ErrorBoundary';
import Bugsnag from '@bugsnag/expo';
import { useLockDeviceOrientation } from './hooks/useLockDeviceOrientation';
import LayoutChangeEventProvider from './components/v2BaseComponents/LayoutChangeEventProvider';
import * as Notifications from 'expo-notifications';
import { linkingOptions } from './utils/linkingOptions';

const intl = getIntl('PrimaryRecordApp', {
    defaultDocumentTitle: 'Primary Record',
});

Notifications.setNotificationHandler({
    handleNotification: async () => ({
        shouldShowBanner: true,
        shouldShowList: true,
        shouldPlaySound: true,
        shouldSetBadge: true,
    }),
});

// I hit issues were double clicking would open 2 auth sessions which pops an error
let isAuthSessionOpen = false;

function modifyAWSExports(exports: any, environment: Environment) {
    // https://docs.amplify.aws/lib/auth/social/q/platform/react-native/#redirect-urls
    const massageRawRedirectUrls = (rawValue: string) => {
        const splitRedirects = rawValue.split(',');
        const getRedirectContaining = (search: string) =>
            splitRedirects.find((redirect: string) => redirect.includes(search));

        const clientTypeRedirects: Record<ClientType, string> = {
            // @ts-expect-error TS2322
            'mobile-dev': getRedirectContaining('primary-record'),
            // @ts-expect-error TS2322
            mobile: getRedirectContaining('primary-record'),
            // @ts-expect-error TS2322
            'local-web': getRedirectContaining('localhost'),
            // @ts-expect-error TS2322
            'dev-web': getRedirectContaining('dev.primaryrecord'),
            // @ts-expect-error TS2322
            'prod-web': getRedirectContaining('www.primaryrecord.app'),
        };
        return clientTypeRedirects[getClientType()];
    };

    const oauthDomains: Record<Environment, string> = {
        dev: 'auth.dev.primaryrecord.app',
        prod: 'auth.primaryrecord.app',
    };

    const mobileUrlOpener = async (url: string, redirectUrl: string) => {
        try {
            if (isAuthSessionOpen) return;

            isAuthSessionOpen = true;
            const result = await WebBrowser.openAuthSessionAsync(url, redirectUrl, {
                showTitle: false,
                preferEphemeralSession: false,
                // duckduckgo, for example, doesn't allow google login at all
                browserPackage: Platform.OS === 'android' ? 'com.android.chrome' : undefined,
            });

            if (result.type === 'success') {
                Linking.openURL(result.url);
            }
        } catch (e) {
            throw e;
        } finally {
            isAuthSessionOpen = false;
        }
    };
    const isMobileClient = getClientType() === 'mobile' || getClientType() === 'mobile-dev';

    const updatedConfig = {
        ...exports,
        oauth: {
            ...(exports?.oauth || {}),
            domain: oauthDomains[environment],
            redirectSignIn: massageRawRedirectUrls(exports.oauth.redirectSignIn),
            redirectSignOut: massageRawRedirectUrls(exports.oauth.redirectSignOut),
            urlOpener: isMobileClient ? mobileUrlOpener : undefined,
        },
        autoSessionRecord: false,
    };

    return updatedConfig;
}

const BugSnagErrorBoundary =
    Platform.OS === 'web' ? Bugsnag.getPlugin('react')?.createErrorBoundary(React) || React.Fragment : React.Fragment;

function PrimaryRecordApp({ environment }: { environment: Environment }) {
    const { isPendingParamCapture } = useCaptureAppQueryParams();

    useLockDeviceOrientation();

    const [amplifyConfigureComplete, setAmplifyConfigureComplete] = useState(false);
    const queryClient = useMemo(() => getQueryClient(true), []);
    useEffect(() => {
        const targetExports = environment === 'dev' ? awsExportsDev : awsExportsProd;
        const modifiedExports = modifyAWSExports(targetExports, environment);
        Amplify.configure(modifiedExports);
        setAmplifyConfigureComplete(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!amplifyConfigureComplete || isPendingParamCapture) return null;

    return (
        <BugSnagErrorBoundary>
            <LayoutChangeEventProvider>
                <StatusBar barStyle="dark-content" translucent />
                <ThemeProvider theme={amplifyUITheme}>
                    <KeyboardRestrictedView>
                        <SafeAreaProvider>
                            <SafeAreaView style={{ width: '100%', height: '100%', backgroundColor: 'white' }}>
                                <KeyboardAvoider>
                                    <EventProvider>
                                        <QueryClientProvider client={queryClient}>
                                            <PortalProvider>
                                                <NavigationContainer
                                                    linking={linkingOptions}
                                                    fallback={
                                                        <AnimateLoadedContent isLoading>
                                                            {() => null}
                                                        </AnimateLoadedContent>
                                                    }
                                                    documentTitle={{
                                                        formatter: (options: any) => {
                                                            return options?.title ?? intl('defaultDocumentTitle');
                                                        },
                                                    }}
                                                >
                                                    <SideDrawerCollapsedStateProvider>
                                                        <FixedPositionPortalContainer>
                                                            <NotificationsProvider>
                                                                <NestedScrollViewPortalHostContainer>
                                                                    <View
                                                                        style={{
                                                                            width: '100%',
                                                                            height: '100%',
                                                                            backgroundColor: colors['Background'],
                                                                        }}
                                                                        testID="primary-record-app-container"
                                                                    >
                                                                        <ErrorBoundary>
                                                                            <RootNavigation />
                                                                        </ErrorBoundary>
                                                                    </View>
                                                                </NestedScrollViewPortalHostContainer>
                                                            </NotificationsProvider>
                                                        </FixedPositionPortalContainer>
                                                    </SideDrawerCollapsedStateProvider>
                                                </NavigationContainer>
                                            </PortalProvider>
                                        </QueryClientProvider>
                                    </EventProvider>
                                </KeyboardAvoider>
                            </SafeAreaView>
                        </SafeAreaProvider>
                    </KeyboardRestrictedView>
                </ThemeProvider>
            </LayoutChangeEventProvider>
        </BugSnagErrorBoundary>
    );
}

export default withIsFormDirtyProvider(PrimaryRecordApp);
